import { useQuery, useQueryClient } from '@tanstack/react-query';
import { orderBy } from 'natural-orderby';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    AccentLabel,
    AnimatedPageLoader,
    HorizontalFlex,
    Input,
    Label,
    PadContainer,
    SingleSelect,
    Ticket,
    TicketBody,
    TicketHeader,
    VerticalFlex,
} from 'smartsuite-ui';
import {
    getReservationStatusUri,
    getReservationToEnrollUri,
} from '../constants/apiConstants.js';
import { airlines, validAirlines } from '../constants/flightEntryConstants';
import { isFlightNumberValid, isPnrValid } from '../utils/validationUtil.js';
function ReservationFlightDetails() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { reservationId, reservationLastName } = useParams();
    const arrivalDate = new URLSearchParams(location.search).get('arrivalDate');
    const [passengers, setPassengers] = useState([]);

    const transformedPassengers = passengers.map((passenger) => ({
        ...passenger,
        airlineCode: passenger?.airline?.airlineCode ?? '',
    }));

    const updatedPassengers = queryClient.getQueryData([
        'reservation',
        reservationId,
        reservationLastName,
        'stateData',
    ])?.passengers;

    const navigateToEnrollmentCard = (httpStatus) => {
        navigate('/waltdisneyworld', { state: { errorStatus: httpStatus } });
        return;
    };

    const reservationQuery = useQuery({
        queryKey: ['reservation', reservationId, reservationLastName],
        queryFn: async () => {
            const response = await fetch(
                getReservationToEnrollUri(
                    reservationId,
                    reservationLastName,
                    arrivalDate,
                ),
            );

            if (!response.ok) {
                navigateToEnrollmentCard(response.status);
                return response;
            }
            const parsedResponse = await response.json();

            if (updatedPassengers) {
                setPassengers(updatedPassengers);
                return parsedResponse;
            }

            const orderedPassengers = orderBy(
                parsedResponse.passengers,
                ['isPrimary'],
                ['desc'],
            );

            setPassengers(orderedPassengers);
            return parsedResponse;
        },
    });

    const isPassengerEligible = (passenger) => {
        return (
            isPnrValid(passenger?.pnr) &&
            validAirlines.includes(passenger.airline?.airlineCode)
        );
    };

    const handlePassengerValueChange = (paxId, field, value) => {
        setPassengers((previousState) => {
            const index = previousState.findIndex(
                (passenger) => passenger.paxId === paxId,
            );

            previousState[index] = {
                ...previousState[index],
                [field]: value,
                isEligible: isPassengerEligible({
                    ...previousState[index],
                    [field]: value,
                }),
            };
            return [...previousState];
        });
    };

    const onAirlineChange = (selectedOption, paxId) => {
        const passenger = passengers.find(
            (passenger) => passenger.paxId === paxId,
        );
        if (selectedOption?.airlineCode === passenger.airline?.airlineCode) {
            return;
        }
        handlePassengerValueChange(paxId, 'airline', selectedOption);
    };

    const handleOptOutChange = (paxId, optedOutTimestamp) => {
        handlePassengerValueChange(
            paxId,
            'optedOutTimestamp',
            optedOutTimestamp,
        );
        handlePassengerValueChange(paxId, 'pnr', '');
        handlePassengerValueChange(paxId, 'airline', null);
        handlePassengerValueChange(paxId, 'flightNumber', '');
    };

    const applyFlightInfoToAll = () => {
        const primaryPassenger = passengers[0];
        if (!primaryPassenger) {
            return;
        }

        const updatedPassengers = passengers.map((passenger, index) => {
            if (index === 0) {
                return passenger;
            }
            return {
                ...passenger,
                airline: primaryPassenger.airline,
                flightNumber: primaryPassenger.flightNumber,
                pnr: primaryPassenger.pnr,
                isEligible: primaryPassenger.isEligible,
                optedOutTimestamp: primaryPassenger.optedOutTimestamp,
            };
        });
        setPassengers(updatedPassengers);
    };

    const numberOfEligiblePassengers = passengers.filter(
        (passenger) => passenger?.isEligible,
    ).length;

    const isEveryPassengerValid = passengers.every(
        (passenger) =>
            (!passenger?.pnr && !passenger?.airline) ||
            passenger?.isEligible ||
            passenger?.airline?.airlineName === 'Other',
    );

    const isNextButtonEnabled =
        numberOfEligiblePassengers > 0 && isEveryPassengerValid;
    const handleSubmit = () => {
        queryClient.setQueryData(
            ['reservation', reservationId, reservationLastName, 'stateData'],
            { ...reservationQuery.data, passengers: transformedPassengers },
        );

        navigate(`../reservationAddressDetails`);
    };

    if (!passengers.length) {
        return <AnimatedPageLoader />;
    }

    const renderPassengerTickets = () => {
        if (!passengers.length) {
            return null;
        }

        return passengers.map((passenger, index) => {
            const showEligibleBadge =
                passenger?.isEligible ||
                passenger?.airline?.airlineName === 'Other';
            const isEligibleString = passenger?.isEligible
                ? 'eligible'
                : 'ineligible';

            const applyAllButton =
                index === 0 && passengers.length > 1 ? (
                    <>
                        <PadContainer cssClassModifiers={['pad-small']} />
                        <HorizontalFlex
                            cssClassModifiers={[
                                'justify-center',
                                'align-center',
                            ]}
                        >
                            <button
                                className="btn btn--primary"
                                onClick={applyFlightInfoToAll}
                                disabled={!passenger?.isEligible}
                            >
                                {t('applyFlightInfoToAllTravelers')}
                            </button>
                        </HorizontalFlex>
                    </>
                ) : null;

            return (
                <div key={passenger.paxId}>
                    <Ticket>
                        <TicketHeader>
                            <VerticalFlex>
                                <AccentLabel>Guest Name</AccentLabel>
                                <Label>{`${passenger.lastName}, ${passenger.firstName}`}</Label>
                            </VerticalFlex>
                            <PadContainer cssClassModifiers={['pad-small']} />
                            {passenger.isPrimary && (
                                <>
                                    <span className="reservation-flight-details__badge reservation-flight-details__badge--primary-guest">
                                        Primary Guest
                                    </span>
                                    <PadContainer
                                        cssClassModifiers={['pad-small']}
                                    />
                                </>
                            )}
                            {showEligibleBadge && (
                                <span
                                    className={`reservation-flight-details__badge reservation-flight-details__badge--${isEligibleString}`}
                                >
                                    {t(isEligibleString)}
                                </span>
                            )}
                        </TicketHeader>
                        <TicketBody>
                            <PadContainer cssClassModifiers={['pad-small']}>
                                <SingleSelect
                                    cssClassModifiers={['small-relative']}
                                    label={t('airlineLabel')}
                                    options={airlines}
                                    isClearable={true}
                                    value={
                                        airlines.length === 1
                                            ? airlines[0]
                                            : passenger.airline || null
                                    }
                                    onChange={(event) =>
                                        onAirlineChange(event, passenger.paxId)
                                    }
                                    placeholder={t('selectAirline')}
                                    textField="airlineName"
                                    valueField="airlineCode"
                                    errorMessage="Please fill out an airline."
                                    isValid={
                                        !isPnrValid(passenger?.pnr) ||
                                        !!passenger.airline
                                    }
                                    disabled={!!passenger?.optedOutTimestamp}
                                />
                                <div className="reservation-flight-details__shared-fields-container">
                                    <Input
                                        cssClassModifiers={['medium-relative']}
                                        label={t('flightNumberLabel')}
                                        placeholder={t(
                                            'flightNumberPlaceholder',
                                        )}
                                        value={passenger.flightNumber}
                                        maxLength={4}
                                        onChange={(event) =>
                                            handlePassengerValueChange(
                                                passenger.paxId,
                                                'flightNumber',
                                                event.target.value,
                                            )
                                        }
                                        errorMessage={
                                            'Enter a 1-4 digit flight number.'
                                        }
                                        isValid={
                                            !passenger.flightNumber ||
                                            isFlightNumberValid(
                                                passenger.flightNumber,
                                            )
                                        }
                                        disabled={
                                            !!passenger?.optedOutTimestamp
                                        }
                                    />
                                    <Input
                                        label={t('pnrLabel')}
                                        placeholder={t('pnrPlaceholder')}
                                        value={passenger.pnr}
                                        maxLength={6}
                                        onChange={(event) =>
                                            handlePassengerValueChange(
                                                passenger.paxId,
                                                'pnr',
                                                event.target.value.toUpperCase(),
                                            )
                                        }
                                        errorMessage={
                                            'Please enter a 6 character alphanumeric PNR.'
                                        }
                                        isValid={
                                            (!passenger.pnr &&
                                                !passenger.airline) ||
                                            passenger?.airline?.airlineName ===
                                                'Other' ||
                                            isPnrValid(passenger.pnr)
                                        }
                                        disabled={
                                            !!passenger?.optedOutTimestamp
                                        }
                                    />
                                </div>
                                <label className="reservation-flight-details__checkbox-container">
                                    <input
                                        type="checkbox"
                                        checked={!!passenger?.optedOutTimestamp}
                                        onChange={() =>
                                            handleOptOutChange(
                                                passenger.paxId,
                                                passenger?.optedOutTimestamp
                                                    ? null
                                                    : new Date().toISOString(),
                                            )
                                        }
                                    />
                                    <span>
                                        Opt Out (Lap Child, Infant, or Not
                                        Flying)
                                    </span>
                                </label>
                            </PadContainer>
                        </TicketBody>
                    </Ticket>
                    {applyAllButton}
                </div>
            );
        });
    };

    return (
        <div className="page-wrapper">
            <div className="reservation-flight-details">
                <div>
                    <h1 className="reservation-flight-details__title">
                        {t('flightInformationTitle')}
                    </h1>
                    <p className="instruction-text">
                        {t('flightInformationInstructions')}{' '}
                        <strong>{t('enrollYourReservationNote')}</strong>
                    </p>
                </div>

                <div className="reservation-flight-details__tickets-container">
                    {renderPassengerTickets()}
                </div>
                <PadContainer cssClassModifiers={['pad-small']} />
                <div className="submit-container">
                    <button
                        className="btn btn--primary"
                        disabled={!isNextButtonEnabled}
                        onClick={handleSubmit}
                    >
                        {`${t('next')} - ${numberOfEligiblePassengers} / ${
                            passengers.length
                        } ${t('eligible')}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ReservationFlightDetails;
